import React from "react";
import { useTranslation } from "react-i18next";
import { Lock, ArrowLeft, Mail } from "lucide-react";
import { Button } from "../ui/Button";
import { cn } from "src/utils/cn";
import { useNavigate } from "react-router-dom";

/**
 * Standardized "no access" screen component
 * Shown when user lacks VIEW permission for a page
 * Follows UX spec: clear explanation, next steps, link back to accessible pages
 */
export interface NoAccessProps {
  subject?: string;
  action?: string;
  className?: string;
  title?: string;
  description?: string;
  showBackButton?: boolean;
  backButtonLabel?: string;
  onBack?: () => void;
}

export const NoAccess: React.FC<NoAccessProps> = ({
  subject = "this page",
  action = "view",
  className,
  title,
  description,
  showBackButton = true,
  backButtonLabel,
  onBack,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const defaultTitle = title || t("no-access");
  const defaultDescription =
    description ||
    t("no-access-description", { action, subject }) ||
    `You don't have permission to ${action} ${subject}. Contact an admin to request access.`;
  const defaultBackLabel = backButtonLabel || t("go-back");

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  return (
    <div
      className={cn(
        "flex items-center justify-center min-h-[500px] bg-gray-50",
        className,
      )}
    >
      <div className="text-center max-w-md px-4">
        <div className="mx-auto h-20 w-20 text-gray-400 mb-6 flex items-center justify-center">
          <Lock className="h-full w-full" />
        </div>
        <h2 className="text-2xl font-semibold text-gray-900 mb-3">
          {defaultTitle}
        </h2>
        <p className="text-base text-gray-600 mb-8">{defaultDescription}</p>
        <div className="space-y-3">
          {showBackButton && (
            <Button
              onClick={handleBack}
              variant="outline"
              leftIcon={<ArrowLeft className="h-4 w-4" />}
              className="w-full"
            >
              {defaultBackLabel}
            </Button>
          )}
          <div className="text-sm text-gray-500">
            <p className="mb-2">{t("need-access")}</p>
            <p>{t("contact-admin-for-access")}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
